/**
 * Registrazione del verdetto su un'allerta.
 *
 * Perche' una modale e non una riga espandibile
 * ---------------------------------------------
 * Decidere qui non e' spuntare una casella. La decisione ha un motivo
 * obbligatorio, una portata da scegliere e un riepilogo di cio' su cui si sta
 * decidendo, e quel riepilogo va **ripetuto davanti agli occhi di chi firma**:
 * resta agli atti, e fra due anni sara' l'unica cosa che spiega perche' un
 * obbligo non ha un sostituto. Una riga di tabella non regge quel peso.
 *
 * Le evidenze lunghe — l'elenco delle annualita', la storia delle decisioni —
 * stanno invece nella riga, dove si leggono senza aprire nulla.
 *
 * La domanda che raccoglie il dato mancante
 * -----------------------------------------
 * «Sostituto non dovuto» e' ambiguo, e l'ambiguita' e' esattamente cio' che al
 * sistema manca: puo' significare «quest'anno non era previsto» oppure «qui i
 * contenuti si accumulano, un sostituto non esiste mai». Sono la stessa frase e
 * due fatti diversi. Il radio della **portata** e' la domanda che li separa, e
 * ogni risposta e' un pezzo della classificazione che il catalogo ANAC non da'.
 *
 * Al redattore la seconda opzione si mostra disabilitata e dichiarata come
 * proposta: la natura di un obbligo spegne una classe di allerte per sempre, e
 * quella parola resta al Super Admin. Ma la proposta si registra — e' agli atti
 * anche se nessuno la conferma.
 */

import { useState } from "react";
import { naturaPertinente } from "../lib/allerte";
import type { Allerta } from "../lib/allerte";
import { CADENZE_OBBLIGO } from "../api/types";
import type {
  CadenzaObbligo,
  EsitoValutazione,
  NaturaObbligo,
  PortataValutazione,
  RichiestaRegola,
  RichiestaValutazione,
  Valutazione,
} from "../api/types";
import { ETICHETTA_NATURA } from "../lib/etichette";
import { validaData } from "../lib/formats";
import { ETICHETTE_CADENZA } from "../lib/scadenze";
import { CampoDato, CampoLibero, CampoScelta } from "./campi";
import { Modale } from "./Modale";
import { Avviso, Bottone, Chip } from "./primitive";

/** Lunghezza minima del motivo, la stessa che l'API impone. */
const MOTIVO_MINIMO = 10;

export function ModaleVerdetto({
  allerta,
  eSuperAdmin,
  verdettiPrecedenti,
  proposta,
  inCorso,
  errore,
  onChiudi,
  onRegistra,
  onDichiara,
}: {
  allerta: Allerta;
  /** Solo il Super Admin puo' dichiarare la natura dell'obbligo. */
  eSuperAdmin: boolean;
  /** Quanti verdetti «non dovuto» sono gia' stati dati su questo obbligo. */
  verdettiPrecedenti: number;
  /**
   * Proposta di natura lasciata da un redattore e non ancora confermata.
   *
   * E' l'anello che mancava: il redattore poteva proporre — l'API conservava la
   * proposta, la modale la raccoglieva — ma nessuno la mostrava al Super Admin,
   * che quindi non poteva confermarla. Una meta' di percorso.
   */
  proposta?: Valutazione;
  inCorso: boolean;
  errore: string | null;
  onChiudi: () => void;
  onRegistra: (dati: RichiestaValutazione) => void;
  /** Dichiarazione della natura dell'obbligo: solo per il Super Admin. */
  onDichiara: (dati: RichiestaRegola) => void;
}) {
  const [esito, setEsito] = useState<EsitoValutazione>("sostituto_non_dovuto");
  // Con una proposta davanti la modale parte da li': e' la ragione per cui il
  // Super Admin la sta aprendo, e fargli ripetere una scelta gia' motivata da
  // un collega sarebbe chiedergli di fingere di non averla letta.
  const [portata, setPortata] = useState<PortataValutazione>(
    proposta && eSuperAdmin ? "obbligo" : "periodo",
  );
  const [natura, setNatura] = useState<NaturaObbligo>(
    (proposta?.natura_proposta as NaturaObbligo | undefined) ?? "accumulativo",
  );
  const [cadenza, setCadenza] = useState<CadenzaObbligo | "">("");
  const [entro, setEntro] = useState("");
  const [motivo, setMotivo] = useState("");

  const inPreparazione = esito === "sostituto_in_preparazione";

  /**
   * Cambiare esito riporta la portata sul periodo.
   *
   * Il campo «Fin dove vale» si smonta quando l'esito diventa «in
   * preparazione», ma lo stato gli sopravviveva: chi aveva scelto «e' la natura
   * di questo obbligo» e poi cambiava esito inviava una dichiarazione
   * permanente con una natura **mai vista** — il valore predefinito di un campo
   * nel frattempo sparito dallo schermo. Una scrittura irreversibile decisa da
   * un residuo.
   */
  const cambiaEsito = (valore: EsitoValutazione) => {
    setEsito(valore);
    if (valore === "sostituto_in_preparazione") {
      setPortata("periodo");
      setCadenza("");
    }
  };

  // Sul file da sostituire la natura non ha nulla da dire: la regola, e il
  // perche', stanno in `lib/allerte` (`naturaPertinente`).
  const naturaAmmessa = naturaPertinente(allerta.tipo);
  const dichiaraNatura =
    naturaAmmessa && portata === "obbligo" && eSuperAdmin && !inPreparazione;
  const erroreData = entro ? validaData(entro) : null;
  const pronto =
    motivo.trim().length >= MOTIVO_MINIMO &&
    (!inPreparazione || (Boolean(entro) && !erroreData));

  const registra = () => {
    // La natura dell'obbligo passa dal proprio endpoint, che e' l'unico a
    // scrivere anche la cadenza — e lascia comunque una riga di giornale.
    if (dichiaraNatura) {
      // Senza una cadenza scelta il campo non si manda: `null` cancellerebbe
      // quella che il Comune avesse gia' dichiarato.
      onDichiara({
        natura,
        ...(cadenza ? { cadenza_dichiarata: cadenza } : {}),
        motivo: motivo.trim(),
      });
      return;
    }
    onRegistra({
      id_nodo: allerta.id_nodo,
      tipo_allerta: allerta.tipo,
      chiave_periodo: allerta.chiave_periodo,
      pubblicazione_id: allerta.pubblicazione_id ?? null,
      esito,
      // Un redattore non dichiara la natura: la propone. La portata resta sul
      // periodo, e l'API rifiuterebbe comunque il contrario.
      portata: "periodo",
      natura_proposta:
        naturaAmmessa && portata === "obbligo" && !inPreparazione ? natura : null,
      motivo: motivo.trim(),
      valida_fino_al: inPreparazione ? entro : null,
      // L'istantanea di cio' che si aveva davanti: il sistema non la rilegge,
      // serve a chi rileggera' la decisione quando i dati saranno cambiati.
      evidenza: {
        motivo_allerta: allerta.motivo,
        termine: allerta.termine ?? null,
        giorni_residui: allerta.giorni_residui,
        cadenza: allerta.cadenza,
        fonte_cadenza: allerta.fonte_cadenza,
        annualita: allerta.evidenza.annualita,
      },
    });
  };

  return (
    <Modale
      titolo={`Verdetto su «${allerta.denominazione_nodo}»`}
      aperta
      onChiudi={onChiudi}
      pedice={
        <>
          <Bottone variante="fantasma" onClick={onChiudi}>
            Annulla
          </Bottone>
          <Bottone
            variante="primario"
            onClick={registra}
            disabled={!pronto || inCorso}
            inCorso={inCorso}
          >
            Registra il verdetto
          </Bottone>
        </>
      }
    >
      <div className="pila">
        {errore ? (
          <Avviso tono="critico" titolo="Registrazione non riuscita">
            {errore}
          </Avviso>
        ) : null}

        {/* Cio' su cui si sta decidendo, ripetuto: e' quello che resta agli atti. */}
        <div>
          <p className="anteprima__etichetta">Su che cosa si decide</p>
          <p>{allerta.motivo}</p>
          <div className="riga" style={{ marginTop: "0.5rem" }}>
            <Chip tono="neutro" pallino={false}>
              periodo {allerta.chiave_periodo}
            </Chip>
            {allerta.termine ? (
              <Chip tono="neutro" pallino={false}>
                termine {allerta.termine}
              </Chip>
            ) : null}
            {allerta.evidenza.annualita.length > 0 ? (
              <Chip tono="neutro" pallino={false}>
                annualita' presenti:{" "}
                {allerta.evidenza.annualita.map((voce) => voce.anno).join(" · ")}
              </Chip>
            ) : null}
          </div>
        </div>

        {proposta ? (
          <Avviso
            tono="attenzione"
            titolo={`Proposta da confermare: ${proposta.natura_proposta}`}
          >
            «{proposta.motivo}» — {proposta.decisa_da}, {proposta.decisa_il}.{" "}
            {eSuperAdmin
              ? "La scelta e' gia' impostata su quella proposta: confermandola diventa la natura dell'obbligo, e questa classe di allerte non si riaprira'."
              : "Attende la conferma di un amministratore: fino a quel momento vale solo per il periodo su cui e' stata data."}
          </Avviso>
        ) : null}

        {verdettiPrecedenti >= 3 ? (
          <Avviso tono="primario" titolo="Hai deciso cosi' altre volte">
            Su questo obbligo sono gia' stati registrati {verdettiPrecedenti} verdetti dello
            stesso tenore. Se la ragione e' la natura dell'obbligo e non la singola annualita',
            conviene dichiararla una volta sola: sotto, «vale per sempre».
          </Avviso>
        ) : null}

        <CampoScelta
          etichetta="Esito"
          valore={esito}
          onCambia={(valore) => cambiaEsito(valore as EsitoValutazione)}
          opzioni={[
            { valore: "sostituto_non_dovuto", etichetta: "Un sostituto non e' dovuto" },
            {
              valore: "sostituto_in_preparazione",
              etichetta: "Il sostituto e' dovuto ed e' in preparazione",
            },
          ]}
          vuoto={null}
        />

        {inPreparazione ? (
          <CampoDato
            etichetta="Atteso entro"
            tipo="data"
            valore={entro}
            onCambia={setEntro}
            obbligatorio
            errore={erroreData}
            aiuto="Alla scadenza l'allerta si riapre da sola: e' il senso della promessa, e senza data sarebbe un silenziamento permanente."
          />
        ) : naturaAmmessa ? (
          <>
            <CampoScelta
              etichetta="Fin dove vale"
              valore={portata}
              onCambia={(valore) => setPortata(valore as PortataValutazione)}
              opzioni={[
                {
                  valore: "periodo",
                  etichetta: `Solo per il periodo ${allerta.chiave_periodo}`,
                },
                {
                  valore: "obbligo",
                  etichetta: eSuperAdmin
                    ? "E' la natura di questo obbligo: vale per sempre"
                    : "E' la natura di questo obbligo — proposta all'amministratore",
                },
              ]}
              vuoto={null}
              aiuto={
                eSuperAdmin
                  ? "La natura dell'obbligo spegne questa classe di allerte per sempre, su tutte le annualita'."
                  : "La natura dell'obbligo la dichiara il Super Admin: la sua scelta resta registrata come proposta, e il verdetto vale intanto per il periodo indicato."
              }
            />

            {portata === "obbligo" ? (
              <CampoScelta
                etichetta="Natura dell'obbligo"
                valore={natura}
                onCambia={(valore) => setNatura(valore as NaturaObbligo)}
                opzioni={(Object.keys(ETICHETTA_NATURA) as NaturaObbligo[]).map((voce) => ({
                  valore: voce,
                  etichetta: ETICHETTA_NATURA[voce],
                }))}
                vuoto={null}
                aiuto={
                  allerta.fonte_cadenza === "sezione"
                    ? `Oggi la cadenza «${allerta.cadenza}» e' ereditata dalla sezione ${allerta.id_nodo_cadenza ?? ""}: e' un'ipotesi, non un dato del catalogo.`
                    : "Il catalogo ANAC non dice se i contenuti di un obbligo si sostituiscano o si accumulino: lo sa il Comune."
                }
              />
            ) : null}

            {dichiaraNatura ? (
              <CampoScelta
                etichetta="Cadenza reale (facoltativa)"
                valore={cadenza}
                onCambia={(valore) => setCadenza(valore as CadenzaObbligo | "")}
                opzioni={CADENZE_OBBLIGO.map((voce) => ({
                  valore: voce,
                  etichetta: ETICHETTE_CADENZA[voce],
                }))}
                vuoto="Lascia com'e'"
                aiuto="Lasciandola cosi' non cambia nulla: resta la cadenza gia' dichiarata o, se non ce n'e', l'ipotesi ereditata dalla sezione. Indicandola, il termine di aggiornamento si calcola su questa."
              />
            ) : null}
          </>
        ) : null}

        <CampoLibero
          etichetta="Motivo"
          valore={motivo}
          onCambia={setMotivo}
          obbligatorio
          multilinea
          errore={
            motivo.length > 0 && motivo.trim().length < MOTIVO_MINIMO
              ? `Almeno ${MOTIVO_MINIMO} caratteri: fra due anni sara' l'unica spiegazione disponibile.`
              : null
          }
          aiuto="Resta agli atti con nome e data: indicare la norma o la circostanza, non «visto»."
        />
      </div>
    </Modale>
  );
}
